import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getCoverageRecomputeStatus,
  startCoverageRecompute,
} from '../services/catalog-operations.service';
import {
  clearPendingRecompute,
  readPendingRecompute,
  savePendingRecompute,
} from '../utils/recompute-persistence';

/** Poll cadence for a running recompute. Coverage reads the whole batch, so it
 *  settles slower than the insights twin — 2s still lands within a tick. */
export const COVERAGE_RECOMPUTE_POLL_MS = 2000;

/** Ceiling on how long a single recompute may stay `RUNNING` before the UI
 *  stops asking. Wider than the insights budget because coverage scans every
 *  enriched row for the run; this exists so a wedged run releases the spinner
 *  rather than polling for the rest of the session. */
export const COVERAGE_RECOMPUTE_POLL_BUDGET_MS = 120_000;

/**
 * How a recompute ended, from the UI's point of view. `notFound` and `timeout`
 * are kept apart from `FAILED`: neither is a failure verdict on the workflow,
 * and both still warrant re-reading the coverage.
 */
export type RecomputeOutcome = 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'notFound' | 'timeout';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Polls one run to a terminal state. `RUNNING` is the only status that
 *  continues the loop — collapsing the other three into it is what would make a
 *  dead run poll forever. Exported so the empty-coverage auto-recovery in
 *  `use-batch-coverage` drives the same status loop rather than a second copy. */
export async function pollCoverageRecomputeUntilSettled(runId: string): Promise<RecomputeOutcome> {
  const deadline = Date.now() + COVERAGE_RECOMPUTE_POLL_BUDGET_MS;
  while (Date.now() < deadline) {
    const run = await getCoverageRecomputeStatus(runId);
    // Null is the absorbed 404 — unknown id, aged-out retention, or another
    // subsidiary's run. Harmless on a run we know started: the rows are already
    // persisted, so read them instead of reporting it.
    if (run === null) return 'notFound';
    if (run.status !== 'RUNNING') return run.status;
    await sleep(COVERAGE_RECOMPUTE_POLL_MS);
  }
  return 'timeout';
}

interface UseCoverageRecomputeResult {
  /** True from the trigger until the run settles — spans the POST, the poll, AND a
   *  poll resumed after a page reload, so one flag drives both the button's spinner
   *  and the tab's "recomputing" state. */
  isRecomputing: boolean;
  /** Fires the recompute. Caller is expected to have confirmed first. */
  start: () => void;
}

/** Surfaces a settled recompute's non-success outcomes as a toast. Kept apart so
 *  the live run and the reload-resume path report failures identically. */
function toastCoverageRecomputeFailure(outcome: RecomputeOutcome): void {
  if (outcome === 'FAILED' || outcome === 'CANCELLED') {
    toast.error(
      outcome === 'CANCELLED'
        ? 'The recompute was cancelled. Coverage signals are unchanged.'
        : 'The recompute failed. Coverage signals are unchanged.',
    );
  }
}

/** `isResuming` → re-polling a run left in-flight by a reload. Tracked per
 *  workflow so a batch switch resets it cleanly. */
interface ResumeState {
  id: string;
  isResuming: boolean;
}

/** Seeds resume state during render (not in an effect) by checking for a persisted
 *  runId, so the loading state is already up on the first paint after a reload. */
function initialResumeState(workflowId: string): ResumeState {
  return {
    id: workflowId,
    isResuming: workflowId ? readPendingRecompute('coverage', workflowId) !== null : false,
  };
}

/**
 * Runs one Coverage Signals recompute end to end: POST to integration-service,
 * poll the Temporal status until it settles, then refresh the coverage rows the
 * section reads back from workflow-service. The live run is a mutation (a one-shot
 * imperative action); a run orphaned by a page reload is picked back up by a
 * render-reconciled resume effect. `isRecomputing` spans both, so one flag drives
 * the button and the tab's "recomputing" state.
 */
export function useCoverageRecompute(
  workflowId: string,
  connectorCode: string,
): UseCoverageRecomputeResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (): Promise<RecomputeOutcome> => {
      const run = await startCoverageRecompute(workflowId, connectorCode);
      // Persist before polling so a reload mid-run can pick the poll back up.
      savePendingRecompute('coverage', workflowId, run.runId);
      const outcome = await pollCoverageRecomputeUntilSettled(run.runId);
      clearPendingRecompute('coverage', workflowId);
      return outcome;
    },
    onSuccess: (outcome) => {
      if (outcome === 'FAILED' || outcome === 'CANCELLED') {
        toastCoverageRecomputeFailure(outcome);
        return;
      }
      if (outcome === 'timeout') {
        // Not a verdict — the workflow may still land. Say so, and refresh
        // below in case it already has.
        toast.warning(
          'The recompute is taking longer than expected. Refresh shortly to see results.',
        );
      } else {
        // `notFound` gets the weaker claim: the rows are there to read, but the
        // 404 means we never actually observed the run complete.
        toast.success(
          outcome === 'COMPLETED' ? 'Coverage signals recomputed.' : 'Coverage signals refreshed.',
        );
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchCoverage(workflowId),
      });
    },
    onError: (error) => {
      // Covers the 422 for a connector with no coverage field registry — a
      // permanent "no coverage for this connector", so the server's message is
      // surfaced rather than a generic retry prompt. Drop the marker so a reload
      // doesn't keep resuming a run we can't observe.
      clearPendingRecompute('coverage', workflowId);
      toast.error(extractApiError(error, 'Could not start the recompute. Please try again.'));
    },
  });

  // Detect a reload-orphaned run at render; reconcile on a batch switch (the
  // codebase's "adjust state during render" pattern, avoiding a setState-in-effect).
  const [resume, setResume] = useState<ResumeState>(() => initialResumeState(workflowId));
  if (resume.id !== workflowId) setResume(initialResumeState(workflowId));
  const activeResume = resume.id === workflowId ? resume : initialResumeState(workflowId);

  // Resume a recompute that was still running when the page was reloaded: re-poll the
  // persisted runId to completion, then refresh and settle, keeping the loading state
  // up across the reload. Failures surface; success is silent (the rows just refresh)
  // to avoid a spurious toast from a stale marker. Stale results are ignored by the
  // `s.id === workflowId` identity guard rather than a cancelled flag.
  useEffect(() => {
    if (!activeResume.isResuming) return;
    void (async () => {
      const runId = readPendingRecompute('coverage', workflowId);
      let outcome: RecomputeOutcome = 'notFound';
      if (runId) {
        try {
          outcome = await pollCoverageRecomputeUntilSettled(runId);
        } catch {
          // A non-404 status error — stop resuming and drop the marker.
          outcome = 'notFound';
        }
        clearPendingRecompute('coverage', workflowId);
      }
      toastCoverageRecomputeFailure(outcome);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchCoverage(workflowId),
      });
      setResume((s) => (s.id === workflowId ? { ...s, isResuming: false } : s));
    })();
  }, [activeResume.isResuming, workflowId, queryClient]);

  return {
    isRecomputing: mutation.isPending || activeResume.isResuming,
    start: () => {
      mutation.mutate();
    },
  };
}
